import { isPlainObject } from '@vestigas/utils';

import { camelcaseKeysFromApi } from '../kyClient';
import {
  buildResponseValidationReport,
  type ResponseValidationLogger,
} from '../safeParseResponse';

import { type ConnectionCheck } from './types';
import { zodConnectionCheckSchema } from './validations';

/**
 * A connection check response that does not have the expected shape. Asking
 * again returns the same shape, so a poll ends on it.
 */
export class ConnectionCheckResponseError extends Error {
  constructor(key: string) {
    super(`${key} response did not match the expected shape`);
    this.name = 'ConnectionCheckResponseError';
  }
}

function toAppCase(value: Record<string, unknown>) {
  return camelcaseKeysFromApi(value) as Record<string, unknown>;
}

/** An option's settings it depends on are keyed by setting names, kept as sent. */
function optionFromApi(option: unknown) {
  if (!isPlainObject(option)) {
    return option;
  }

  const { depends_on: dependsOn, ...rest } = option;

  return dependsOn === undefined ? option : { ...rest, dependsOn };
}

function optionsFromApi(options: unknown) {
  return isPlainObject(options)
    ? Object.fromEntries(
        Object.entries(options).map(([setting, list]) => [
          setting,
          Array.isArray(list) ? list.map(optionFromApi) : list,
        ]),
      )
    : options;
}

/**
 * A connection check in the app's case. Options and suggestions are keyed by
 * setting names, e.g. a template's own field keys, so those keys stay as sent.
 */
function connectionCheckFromApi(response: unknown) {
  if (!isPlainObject(response)) {
    return response;
  }

  const { result, ...check } = response;

  if (!isPlainObject(result)) {
    return { ...toAppCase(check), result };
  }

  const { options, suggestions, ...rest } = result;

  return {
    ...toAppCase(check),
    result: {
      ...toAppCase(rest),
      options: optionsFromApi(options),
      suggestions,
    },
  };
}

/**
 * Converts and validates a connection check strictly, as its state and status
 * decide whether a form saves. Drift is reported here.
 *
 * @param response - The response body, in the API's case or, for a check
 *   without options, in the app's.
 * @param options.key - Stable key the drift report is grouped by.
 * @param options.logger - Receives the drift report.
 * @throws ConnectionCheckResponseError when the response has another shape.
 */
export function parseConnectionCheck(
  response: unknown,
  { key, logger }: { key: string; logger: ResponseValidationLogger },
): ConnectionCheck {
  const parsed = zodConnectionCheckSchema.safeParse(
    connectionCheckFromApi(response),
  );

  if (!parsed.success) {
    logger.responseValidation({
      ...buildResponseValidationReport(parsed.error.issues),
      key,
      message: `${key} response has validation errors`,
    });

    throw new ConnectionCheckResponseError(key);
  }

  return parsed.data;
}
